// frontend/src/context/AuthContext.js
import React, { createContext, useEffect, useState, useContext } from 'react';
import getSocket from '../socket';
import { API_URL } from '../config';
import { getUserId } from '../utils/userId';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

// --- Token rehydrieren (bei App-Start) ---
  useEffect(() => {
    async function rehydrate() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }
        // Versuche /api/.../me zu holen; wenn es die Route nicht gibt, dekodiere JWT        
        let me = null;
        const mePaths = ['/api/auth/me', '/api/me', '/auth/me', '/users/me'];
        for (const p of mePaths) {
          try {
            const res = await fetch(`${API_URL}${p}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) { me = await res.json(); break; }
          } catch (_) {}
        }
 

        if (!me) {
          // Fallback: JWT grob dekodieren (ohne Verifikation)
          const [, payload] = token.split('.');
          if (payload) {
            const json = JSON.parse(atob(payload));
            // Häufige Formen: { user: { id, _id, username, email } } oder { id, username }
            me = json?.user || json || null;
          }
        }
        if (me) setUser(me);
      } catch (e) {
        console.warn('rehydrate failed', e);
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    }
    rehydrate();
  }, []);

  // Globaler Presence-Join/Heartbeat – an Login gebunden, unabhängig vom Chat
  useEffect(() => {
    const uid = getUserId(user);
    let hb;
    if (uid) {
      const socket = getSocket();           // jetzt lazy, erst nach Login/Token
      socket.emit('join', uid);
      hb = setInterval(() => socket.emit('presence:heartbeat', { userId: uid }), 15000); // Reduced from 20s to 15s for more reliable presence
    }
    return () => { if (hb) clearInterval(hb); };
  }, [user]);


  // --- Login / Logout API ---
  // Overload: login(email, password)  ODER  login(token, userObject) (Kompatibilität zu altem Code)
  async function login(a, b) {
    try {
      // --- Fall 1: alter Aufruf login(token, user) ---
      if (typeof a === 'string' && typeof b === 'object' && a.includes('.')) {
        const token = a;
        const u = b || {};
        localStorage.setItem('token', token);
        setUser(u);
        const socket = getSocket();
        const uid = getUserId(u);
        if (uid) {
          socket.emit('join', uid);
          socket.emit('presence:heartbeat', { userId: uid });
        }
        return { ok: true };
      }

      // --- Fall 2: regulär login(email, password) ---
      const email = a;
      const password = b;

      // Pfad-/Payload-Varianten abdecken
      const paths = ['/api/auth/login', '/api/login', '/auth/login', '/users/login'];
      const bodies = [
        { email, password },
        { username: email, password },
        { usernameOrEmail: email, password },
      ];

      let token, u, lastError;
      for (const path of paths) {
        for (const body of bodies) {
          try {
            const res = await fetch(`${API_URL}${path}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) { 
              // Handle email verification error specifically
              if (data?.errorType === 'email_not_verified') {
                return {
                  ok: false,
                  error: data.msg,
                  errorType: 'email_not_verified',
                  email: data.email,
                  username: data.username
                };
              }
              // Handle account banned error
              if (data?.errorType === 'account_banned') {
                return {
                  ok: false,
                  error: data.msg,
                  errorType: 'account_banned',
                  banDetails: data.banDetails
                };
              }
              lastError = data?.msg || data?.error || res.statusText; 
              continue; 
            }
            token = data?.token || data?.jwt || data?.accessToken;
            u = data?.user || data?.profile || (data?.username ? data : null);
            if (token) break;
          } catch (e) {
            lastError = e.message;
          }
        }
        if (token) break;
      }

      if (!token) throw new Error(lastError || 'Login failed');
      localStorage.setItem('token', token);
      if (!u) {
        // User nachladen
        try {
          const meRes = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (meRes.ok) u = await meRes.json();
        } catch (_) {}
      }
      if (!u) {
        // JWT-Payload als Fallback
        const [, payload] = token.split('.');
        if (payload) {
          const json = JSON.parse(atob(payload));
          u = json?.user || json || {};
        }
      }
      setUser(u || {});

      // Presence sofort aktivieren
      const socket = getSocket();
      const uid = getUserId(u);
      if (uid) {
        socket.emit('join', uid);
        socket.emit('presence:heartbeat', { userId: uid });
      }
      return { ok: true };
    } catch (e) {
      console.error('login error:', e);
      return { ok: false, error: e.message || 'Server error' };
    }
  }

  function logout() {
    try {
      localStorage.removeItem('token');
      setUser(null);
      // Optional: Socket getrennt lassen, da andere Bereiche evtl. noch WS brauchen
    } catch (e) {
      console.warn('logout error', e);
    }
  }

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading, login, logout }}>
       {children}
     </AuthContext.Provider>
   );
 };

// kleines Komfort-Hook
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
